"use client"

import { useCallback, useState } from "react"
import type { BooruPost } from "@/lib/booru-api"
import type { AudioChanPost } from "@/lib/audiochan-types"
import { useSibStore, audioChanPostToTrack } from "@/lib/sib-store"
import { useScriptLinkingStore } from "@/lib/script-linking-store"
import { cn } from "@/lib/utils"
import { Play, Pause, Plus, Music, Clock, ExternalLink, PenLine, Search } from "@/lib/icons"
import { Button } from "@/components/ui/button"
import { AudioScriptPanel } from "@/components/audio-script-panel"
import { toast } from "sonner"
import { AudioScripter } from "@/components/editors/audio-scripter"
import { useTranslations } from "next-intl"

interface AudioCardProps {
  post: BooruPost
  onTagClick?: (tag: string) => void
  className?: string
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return "0:00"
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, "0")}`
}

function generateWaveform(seed: string, bars = 52): number[] {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i)
    hash |= 0
  }
  return Array.from({ length: bars }, (_, i) => {
    const v = Math.abs(Math.sin(hash * (i + 1) * 0.31) * 0.65 + Math.cos(hash * (i + 1) * 0.19) * 0.35)
    return Math.max(0.08, Math.min(1, v))
  })
}

const AUDIENCE_KEYS = new Set([
  "F4M","F4F","F4A","F4TF","M4F","M4M","M4A","4M","4F","4NB","4T",
])

const TAG_PREVIEW_COUNT = 8

export function AudioCard({ post, onTagClick, className }: AudioCardProps) {
  const tt = useTranslations("audioCard")
  const rawPost = post.rawPost as AudioChanPost | undefined
  const { currentTrack, isPlaying, play, pause, resume, addToQueue } = useSibStore()
  const [tagsExpanded, setTagsExpanded] = useState(false)
  const [scriptPanelOpen, setScriptPanelOpen] = useState(false)
  const [scripterOpen, setScripterOpen] = useState(false)
  // AudioChan items cannot be streamed by us (its audio address is locked to its own site), so they play inside AudioChan's own
  // official embeddable player (its oEmbed gives https://audiochan.com/embed/a/<id>?mode=player), shown inside the card.
  const [embedOpen, setEmbedOpen] = useState(false)

  const isCurrentTrack = currentTrack?.id === (rawPost?.id || post.id.replace("audiochan-", ""))
  const isThisPlaying = isCurrentTrack && isPlaying
  const waveform = generateWaveform(post.id)

  const audienceTypes: string[] = rawPost?.audienceType?.length
    ? rawPost.audienceType
    : post.tags.filter((t) => AUDIENCE_KEYS.has(t.toUpperCase()))

  const contentTags = post.tags.filter(
    (t) => !AUDIENCE_KEYS.has(t.toUpperCase()) && t.toUpperCase() !== "SFW" && t.toUpperCase() !== "NSFW",
  )
  const visibleTags = tagsExpanded ? contentTags : contentTags.slice(0, TAG_PREVIEW_COUNT)
  const extraTagCount = contentTags.length - TAG_PREVIEW_COUNT
  const isNsfw = rawPost?.isNsfw ?? post.rating === "explicit"
  const duration = rawPost?.duration || post.audioDuration || 0
  const artist = rawPost?.artist || "Unknown Artist"

  // The audio's id on AudioChan, taken from its page address (https://audiochan.com/a/<id>); only that host is ever embedded.
  const embedId = (() => {
    try {
      const u = new URL(post.postUrl || "")
      const m = u.hostname === "audiochan.com" ? u.pathname.match(/^\/a\/([A-Za-z0-9_-]+)$/) : null
      return m ? m[1] : null
    } catch {
      return null
    }
  })()

  const handlePlayPause = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    // AudioChan locks playback to its own site: for these, play = show AudioChan's own player in the card.
    if (rawPost?.externalOnly) {
      if (embedId) setEmbedOpen((open) => !open)
      else if (post.postUrl) window.open(post.postUrl, "_blank", "noopener")
      return
    }
    if (isCurrentTrack) { isPlaying ? pause() : resume() }
    else if (rawPost) { play(audioChanPostToTrack(rawPost)) }
  }, [isCurrentTrack, isPlaying, pause, resume, play, rawPost, post.postUrl, embedId])

  const handleAddToQueue = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (rawPost) {
      addToQueue(audioChanPostToTrack(rawPost))
      toast.success(tt("addedToQueue"), { description: rawPost.title, duration: 2000 })
    }
  }, [addToQueue, rawPost])

  const handleArtistClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (artist && artist !== "Unknown Artist") onTagClick?.(artist)
  }, [artist, onTagClick])

  const handleCreateScript = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (!rawPost) return
    setScripterOpen(true)
  }, [rawPost])

  const handleFindScripts = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setScriptPanelOpen(true)
  }, [])

  return (
    <div className={cn(
      "relative w-full rounded-xl overflow-hidden group transition-all duration-200",
      isCurrentTrack
        ? "bg-[#1e1535] border border-purple-600/50 shadow-lg shadow-purple-900/20"
        : "bg-[#18181f] border border-white/[0.07] hover:border-purple-700/40 hover:bg-[#1e1e2a]",
      className,
    )}>

      {/* Waveform — reduced height so it never reaches text */}
      <div className="absolute bottom-0 left-0 right-0 h-10 flex items-end gap-px px-3 pointer-events-none overflow-hidden">
        {waveform.map((h, i) => (
          <div key={i} className="flex-1 rounded-t-sm min-w-0" style={{
            height: `${h * 100}%`,
            background: isCurrentTrack
              ? `rgba(139,92,246,${0.13 + h * 0.1})`
              : `rgba(139,92,246,${0.06 + h * 0.05})`,
          }} />
        ))}
      </div>

      {isCurrentTrack && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-500 via-violet-400 to-purple-500" />
      )}

      {/* Duration + actions — inline top-right, no absolute */}

      {/* Card body */}
      <div className="relative z-10 px-3 py-2.5 flex gap-2.5">

        {/* Avatar */}
        <div
          onClick={handleArtistClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && handleArtistClick(e as any)}
          title={`Search: ${artist}`}
          className="shrink-0 self-start mt-0.5 rounded-full ring-2 ring-transparent hover:ring-purple-500/50 transition-all cursor-pointer outline-none"
        >
          {rawPost?.artistAvatar ? (
            <img src={rawPost.artistAvatar} alt={artist}
              className="h-10 w-10 rounded-full object-cover border-2 border-white/10" />
          ) : (
            <div className="h-10 w-10 rounded-full bg-purple-900/40 flex items-center justify-center border-2 border-purple-800/30">
              <Music className="h-4 w-4 text-purple-400" />
            </div>
          )}
        </div>

        {/* Middle column — badges, title, artist, tags */}
        <div className="flex-1 min-w-0 flex flex-col">

          {/* Top row — badges on the left; duration and the toolbar on the right, on ONE level. On a very thin screen the right
              group wraps onto its own line under the badges, so nothing is cut off. */}
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 mb-1 min-h-7">
            {audienceTypes.map((type) => (
              <span key={type} className="text-[11px] px-2 py-0.5 rounded font-semibold border bg-[#6d28d9]/70 text-purple-200 border-purple-600/50 shrink-0">
                {type}
              </span>
            ))}
            {isNsfw ? (
              <span className="text-[11px] px-2 py-0.5 rounded font-semibold border bg-rose-950/70 text-rose-300 border-rose-700/50 shrink-0">NSFW</span>
            ) : (
              <span className="text-[11px] px-2 py-0.5 rounded font-semibold border bg-emerald-950/60 text-emerald-400 border-emerald-700/40 shrink-0">SFW</span>
            )}
            <div className="ml-auto flex items-center gap-1 shrink-0">
              <Clock className="h-3 w-3 text-white/30" />
              <span className="mr-1 text-[11px] text-white/40 tabular-nums">{formatTime(duration)}</span>
              {/* Toolbar: shown on hover with a mouse, always shown on touch screens (they have no hover) */}
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity">
                <Button variant="ghost" size="icon"
                  className="h-7 w-7 text-white/30 hover:text-purple-300 hover:bg-purple-900/30"
                  onClick={handleAddToQueue} title={tt("addToQueue")}>
                  <Plus className="h-3.5 w-3.5" />
                </Button>
                {post.postUrl && (
                  <Button variant="ghost" size="icon"
                    className="h-7 w-7 text-white/30 hover:text-white hover:bg-white/10"
                    onClick={(e) => { e.stopPropagation(); window.open(post.postUrl, "_blank", "noopener") }}
                    title={tt("openOnAudioChan")}>
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button variant="ghost" size="icon"
                  className="h-7 w-7 text-white/30 hover:text-purple-300 hover:bg-purple-900/30"
                  onClick={handleCreateScript} title={tt("createScript")}>
                  <PenLine className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon"
                  className="h-7 w-7 text-white/30 hover:text-purple-300 hover:bg-purple-900/30"
                  onClick={handleFindScripts} title={tt("findScripts")}>
                  <Search className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Title */}
          <p className={cn(
            "text-[15px] font-semibold leading-snug",
            isCurrentTrack ? "text-purple-100" : "text-white/90",
          )}>
            {post.title || "Untitled"}
          </p>

          {/* Artist — div instead of button to avoid browser color resets */}
          <div
            onClick={handleArtistClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleArtistClick(e as any)}
            className="text-[14px] font-medium !text-white/70 hover:!text-purple-300 transition-colors text-left mt-1.5 mb-2.5 cursor-pointer select-none outline-none py-[5px]"
            title={`Search: ${artist}`}
          >
            {artist}
          </div>

          {/* Tags — fully contained in this column */}
          <div className="flex flex-wrap gap-2 pb-1">
            {visibleTags.map((tag) => (
              <button
                key={tag}
                className="text-[10.5px] font-semibold px-2 py-[3px] rounded-md border leading-none transition-colors bg-[#2a2a3a] text-white/60 border-white/[0.09] hover:bg-[#3a2a5a] hover:text-purple-200 hover:border-purple-600/40"
                onClick={(e) => { e.stopPropagation(); onTagClick?.(tag) }}
              >
                {tag}
              </button>
            ))}
            {extraTagCount > 0 && !tagsExpanded && (
              <button
                className="text-[10.5px] font-semibold px-2 py-[3px] rounded-md border leading-none transition-colors bg-[#2a2a3a] text-white/35 border-white/[0.06] hover:bg-[#3a2a5a] hover:text-white/60"
                onClick={(e) => { e.stopPropagation(); setTagsExpanded(true) }}
              >
                {tt("extraTagCountMore", { extraTagCount })}
              </button>
            )}
            {tagsExpanded && (
              <button
                className="text-[10.5px] font-semibold px-2 py-[3px] rounded-md border leading-none transition-colors bg-[#2a2a3a] text-white/35 border-white/[0.06] hover:bg-[#3a2a5a] hover:text-white/60"
                onClick={(e) => { e.stopPropagation(); setTagsExpanded(false) }}
              >
                {tt("showLess")}
              </button>
            )}
          </div>
        </div>

        {/* Right column — just the play button, bottom-aligned (the toolbar moved up next to the duration) */}
        <div className="shrink-0 flex flex-col items-end justify-end self-stretch py-0.5">
          {/* Play button — always visible, bottom */}
          <button
            onClick={handlePlayPause}
            className={cn(
              "h-8 w-8 rounded-full flex items-center justify-center transition-all",
              isThisPlaying || embedOpen
                ? "bg-purple-600 text-white shadow-lg shadow-purple-900/40 hover:bg-purple-500"
                : "bg-white/10 text-white/70 hover:bg-purple-700/60 hover:text-white border border-white/10 hover:border-purple-500/50",
            )}
            aria-label={isThisPlaying || embedOpen ? tt("pause") : tt("play")}
          >
            {isThisPlaying || embedOpen ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 ml-0.5" />}
          </button>
        </div>

      </div>

      {/* AudioChan's own player (their official embed), opened by the play button */}
      {embedOpen && embedId && (
        <div className="relative z-10 px-3 pb-3">
          {/* Centred, same space on both sides, both corners rounded (the clip is on the wrapper, not the frame) */}
          {/* AudioChan's embed leaves a fixed 10px black band on its right edge: the frame is 10px wider than this clip so the band is cut off */}
          <div className="mx-auto w-full max-w-[590px] overflow-hidden rounded-xl">
          <iframe
            src={`https://audiochan.com/embed/a/${embedId}?mode=player`}
            title={post.title || "AudioChan player"}
            className="block border-0"
            style={{ width: "calc(100% + 10px)", maxWidth: "none" }}
            height={222}
            allow="autoplay; encrypted-media"
            allowFullScreen
            sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
          />
          </div>
        </div>
      )}

      {/* Find Scripts panel */}
      {rawPost && (
        <AudioScriptPanel
          open={scriptPanelOpen}
          onOpenChange={setScriptPanelOpen}
          audioSourceId={`audiochan:${rawPost.id}`}
          audioTitle={rawPost.title}
        />
      )}

      {/* Audio Scripter — full screen editor */}
      {scripterOpen && rawPost && (
        <AudioScripter
          audioSourceId={`audiochan:${rawPost.id}`}
          audioTitle={rawPost.title}
          audioArtist={rawPost.artist}
          audioDuration={rawPost.duration}
          waveformData={(rawPost as any).rawPost?.audioFile?.waveform || []}
          onClose={() => setScripterOpen(false)}
        />
      )}
    </div>
  )
}
